import React, { useState, useEffect, useRef, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import './install-prompt.css';

function isIOSSafari() {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  // Chrome on iOS includes "CriOS", which doesn't support PWA install
  const isChromeIOS = /CriOS/.test(ua);
  return isIOS && !isChromeIOS;
}

function isStandalone() {
  return window.navigator.standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches;
}

// Capture the event globally — it fires once on page load regardless of auth
let capturedPromptEvent = null;
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  capturedPromptEvent = e;
});

const ShareIcon = () => (
  <svg
    className="install-prompt-share-icon"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#1a1a2e"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
    <polyline points="16 6 12 2 8 6" />
    <line x1="12" y1="2" x2="12" y2="15" />
  </svg>
);

export default function InstallPrompt() {
  const { currentUser } = useContext(AuthContext);
  const [visible, setVisible] = useState(false);
  const [variant, setVariant] = useState(null); // 'android' | 'ios'
  const deferredPrompt = useRef(null);

  useEffect(() => {
    if (!currentUser) return;
    if (localStorage.getItem('lightway-install-dismissed') === 'true') return;
    if (isStandalone()) return;

    const showTimer = setTimeout(() => {
      if (capturedPromptEvent) {
        deferredPrompt.current = capturedPromptEvent;
        setVariant('android');
        setVisible(true);
      } else if (isIOSSafari()) {
        setVariant('ios');
        setVisible(true);
      }
    }, 2000);

    const handleShowInstall = () => {
      if (isStandalone()) return;
      if (capturedPromptEvent) {
        deferredPrompt.current = capturedPromptEvent;
        setVariant('android');
        setVisible(true);
      } else if (isIOSSafari()) {
        setVariant('ios');
        setVisible(true);
      }
    };
    window.addEventListener('lightway-show-install', handleShowInstall);

    return () => {
      clearTimeout(showTimer);
      window.removeEventListener('lightway-show-install', handleShowInstall);
    };
  }, [currentUser]);

  const dismiss = () => {
    localStorage.setItem('lightway-install-dismissed', 'true');
    setVisible(false);
  };

  const handleInstall = async () => {
    if (!deferredPrompt.current) return;
    deferredPrompt.current.prompt();
    await deferredPrompt.current.userChoice;
    deferredPrompt.current = null;
    capturedPromptEvent = null;
    dismiss();
  };

  if (!visible || !variant) return null;

  return (
    <div className="install-prompt-overlay" onClick={dismiss}>
      <div className="install-prompt-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="install-prompt-handle" />
        <img
          className="install-prompt-logo"
          src="/logo192.png"
          alt="Lightway"
        />
        <h2 className="install-prompt-title">Install Lightway</h2>

        {variant === 'android' && (
          <>
            <p className="install-prompt-desc">
              Add Lightway to your home screen for quick access — no app store needed.
            </p>
            <button className="install-prompt-btn" onClick={handleInstall}>
              Add to Home Screen
            </button>
            <button className="install-prompt-dismiss" onClick={dismiss}>
              Not Now
            </button>
          </>
        )}

        {variant === 'ios' && (
          <>
            <p className="install-prompt-desc">
              Add Lightway to your home screen for quick access.
            </p>
            <ol className="install-prompt-steps">
              <li className="install-prompt-step">
                <span className="install-prompt-step-num">1</span>
                <span className="install-prompt-step-text">
                  Tap the <strong>Share</strong> button in the toolbar
                  <ShareIcon />
                </span>
              </li>
              <li className="install-prompt-step">
                <span className="install-prompt-step-num">2</span>
                <span className="install-prompt-step-text">
                  Scroll down and tap <strong>&ldquo;Add to Home Screen&rdquo;</strong>
                </span>
              </li>
              <li className="install-prompt-step">
                <span className="install-prompt-step-num">3</span>
                <span className="install-prompt-step-text">
                  Tap <strong>&ldquo;Add&rdquo;</strong> in the top right
                </span>
              </li>
            </ol>
            <button className="install-prompt-dismiss" onClick={dismiss}>
              Got It
            </button>
          </>
        )}
      </div>
    </div>
  );
}
